import React, { useMemo } from "react";

// =============================================================================
// VoiceSignature — the "voice fingerprint" hexagon.
//
// Renders the CopyTwin's voice as a single, completely unique GEOMETRIC FORM:
// a 6-axis radar polygon. No two trained twins share a shape, so the signature
// reads at a glance as *this person's* voice — not a generic chart.
//
// Each of the 6 vertices is one voice dimension (0..1). The polygon connecting
// them is filled with a brand-blue membrane and traced with a luminous edge,
// sitting inside a faint hexagonal lattice. Untrained twins show the dormant
// lattice only ("awaiting your voice").
//
// Pure SVG + CSS. Geometry is deterministic so the same data always yields the
// same form — the shape becomes the twin's recognizable identity.
// =============================================================================

// Clockwise from the top vertex.
const ANGLES = [0, 1, 2, 3, 4, 5].map((i) => (-90 + i * 60) * (Math.PI / 180));

// Floor so a near-zero dimension still contributes a visible vertex — the
// polygon stays a "form", never collapses into a spike or a dot.
const RADIUS_FLOOR = 0.16;

const vertex = (cx, cy, r, i) => [
  cx + r * Math.cos(ANGLES[i]),
  cy + r * Math.sin(ANGLES[i]),
];

// Ring polygon (the lattice) at a uniform scale of the max radius.
const ringPoints = (cx, cy, R, scale) =>
  ANGLES.map((_, i) => vertex(cx, cy, R * scale, i).join(","))
    .join(" ");

// The voice shape — per-vertex radius driven by the data values.
const shapePoints = (cx, cy, R, values) =>
  ANGLES.map((_, i) => {
    const v = Math.max(0, Math.min(1, values[i] ?? 0));
    const r = R * (RADIUS_FLOOR + (1 - RADIUS_FLOOR) * v);
    return vertex(cx, cy, r, i).join(",");
  }).join(" ");

const CX = 120;
const CY = 98;
const R = 60;

// Strength ramp: light grey-blue (a lighter expression) → brand blue →
// deep petrol (a strong, defining trait). The colour itself tells you where
// the voice leans hard and where it's lighter — no legend needed.
const strengthColor = (v) => {
  if (v >= 0.72) return "#2a6776"; // --darkblue — peak
  if (v >= 0.54) return "#3f7d92";
  if (v >= 0.38) return "#6897B7"; // --blue — solid
  if (v >= 0.22) return "#93b6cf";
  return "#c2d2de"; // faint — a lighter expression
};

// Labels never drop below a legible mute grey, but strong axes earn the
// brand colour so the wording reinforces the shape.
const labelColor = (v) => {
  if (v >= 0.54) return "#2a6776";
  if (v >= 0.34) return "#6897B7";
  return "#9aa8b5"; // --cca-text-mute
};

const VoiceSignature = ({ values = null, axes = [], showLabels = true }) => {
  const active = Array.isArray(values) && values.length === 6;

  const shape = useMemo(
    () => (active ? shapePoints(CX, CY, R, values) : null),
    [active, values],
  );

  return (
    <div className="cca-sig" data-active={active ? "1" : "0"}>
      <svg
        className="cca-sig-svg"
        viewBox="0 0 240 196"
        role="img"
        aria-label="Voice signature"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="ccaSigFill" cx="50%" cy="42%" r="62%">
            <stop offset="0%" stopColor="rgba(143,192,236,0.55)" />
            <stop offset="60%" stopColor="rgba(104,151,183,0.34)" />
            <stop offset="100%" stopColor="rgba(42,103,118,0.18)" />
          </radialGradient>
          <linearGradient id="ccaSigStroke" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8fc0ec" />
            <stop offset="55%" stopColor="#6da7ed" />
            <stop offset="100%" stopColor="#2a6776" />
          </linearGradient>
        </defs>

        {/* faint hexagonal lattice */}
        <g className="cca-sig-grid">
          {[1, 0.66, 0.33].map((s) => (
            <polygon key={s} points={ringPoints(CX, CY, R, s)} className="cca-sig-ring" />
          ))}
          {ANGLES.map((_, i) => {
            const [x, y] = vertex(CX, CY, R, i);
            return (
              <line key={i} x1={CX} y1={CY} x2={x} y2={y} className="cca-sig-spoke" />
            );
          })}
        </g>

        {/* the unique voice form */}
        {active && (
          <g className="cca-sig-shape">
            <polygon points={shape} className="cca-sig-fill" />
            <polygon points={shape} className="cca-sig-edge" />
            {ANGLES.map((_, i) => {
              const v = Math.max(0, Math.min(1, values[i] ?? 0));
              const r = R * (RADIUS_FLOOR + (1 - RADIUS_FLOOR) * v);
              const [x, y] = vertex(CX, CY, r, i);
              return (
                <circle
                  key={i}
                  cx={x}
                  cy={y}
                  r={2.3 + v * 2.1}
                  className="cca-sig-node"
                  style={{ fill: strengthColor(v) }}
                />
              );
            })}
          </g>
        )}

        <circle cx={CX} cy={CY} r={2} className="cca-sig-core" />

        {/* axis labels around the lattice */}
        {showLabels &&
          axes.length === 6 &&
          ANGLES.map((a, i) => {
            const [x, y] = vertex(CX, CY, R + 15, i);
            const cos = Math.cos(a);
            const sin = Math.sin(a);
            const anchor = cos > 0.3 ? "start" : cos < -0.3 ? "end" : "middle";
            const dy = sin < -0.5 ? -2 : sin > 0.5 ? 7 : 3;
            const v = active ? Math.max(0, Math.min(1, values[i] ?? 0)) : 0;
            return (
              <text
                key={i}
                x={x}
                y={y + dy}
                className="cca-sig-label"
                textAnchor={anchor}
                style={active ? { fill: labelColor(v) } : undefined}
              >
                {axes[i].label}
              </text>
            );
          })}
      </svg>
    </div>
  );
};

export default VoiceSignature;
